var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { jsx as _jsx } from "react/jsx-runtime";
import { boundMethod } from 'autobind-decorator';
import _isEqual from 'lodash/isEqual';
import memoize from 'memoize-one';
import { PureComponent } from 'react';
import { flowsApi } from './api/flows';
const { createFlow, getFlow, getFlows, removeFlow, updateFlow } = flowsApi;
export const withFlows = (Component, settings = {}) => {
    class WithFlows extends PureComponent {
        constructor(props) {
            super(props);
            this.state = {
                item: null,
                items: [],
                params: Object.assign({}, settings.initialParams),
                createAsyncStatus: 'succeeded',
                getItemAsyncStatus: 'succeeded',
                getItemsAsyncStatus: 'succeeded',
                removeAsyncStatus: 'succeeded',
                updateAsyncStatus: 'succeeded',
            };
            /** We have to bind it like this. Using @boundMethod won't work */
            this.memoizedGetInjectedProps = memoize(this.memoizedGetInjectedProps, _isEqual).bind(this);
        }
        create(data) {
            this.setState({ createAsyncStatus: 'loading' });
            return createFlow(data)
                .then((response) => {
                this.setState({ createAsyncStatus: 'succeeded' });
                return response;
            })
                .catch((error) => {
                this.setState({ createAsyncStatus: 'failed' });
                throw error;
            });
        }
        getItem(flowId) {
            this.setState({ getItemAsyncStatus: 'loading' });
            return getFlow(flowId)
                .then((response) => {
                this.setState({
                    getItemAsyncStatus: 'succeeded',
                    item: response.data,
                });
                return response;
            })
                .catch((error) => {
                this.setState({ getItemAsyncStatus: 'failed' });
                throw error;
            });
        }
        getItems() {
            this.setState({ getItemsAsyncStatus: 'loading' });
            const params = Object.assign({}, this.state.params);
            return getFlows(params)
                .then((response) => {
                var _a;
                const { data } = response;
                this.setState({
                    getItemsAsyncStatus: 'succeeded',
                    items: (_a = data.conversation_flows) !== null && _a !== void 0 ? _a : data.flows,
                });
                return response;
            })
                .catch((error) => {
                this.setState({ getItemsAsyncStatus: 'failed' });
                throw error;
            });
        }
        update(flowId, flow) {
            this.setState({ updateAsyncStatus: 'loading' });
            return updateFlow(flowId, flow)
                .then((response) => {
                const { data: updatedItem } = response;
                /*
                 * Due to backend not immediately updating the response
                 * for `getItems` when requesting them after an update request,
                 * we update the returned item in-state immediately after a successful update.
                 */
                this.setState(({ items: stateItems }) => {
                    const items = [...stateItems];
                    const foundItem = items.findIndex(({ id }) => id === updatedItem.id);
                    items[foundItem] = updatedItem;
                    return { updateAsyncStatus: 'succeeded', items };
                });
                return response;
            })
                .catch((error) => {
                this.setState({ updateAsyncStatus: 'failed' });
                throw error;
            });
        }
        remove(flowId) {
            this.setState({ removeAsyncStatus: 'loading' });
            return removeFlow(flowId)
                .then((response) => {
                /*
                 * Same issue as with `update` method,
                 * the backend won't immediately update the `getItems` response,
                 * so we remove it here manually upon successful response.
                 */
                this.setState(({ items: stateItems }) => {
                    const items = stateItems.filter(({ id }) => id !== flowId);
                    return { removeAsyncStatus: 'succeeded', items };
                });
                return response;
            })
                .catch((error) => {
                this.setState({ removeAsyncStatus: 'failed' });
                throw error;
            });
        }
        paramsChange(params) {
            return new Promise((resolve) => {
                this.setState(({ params: stateParams }) => ({
                    params: Object.assign(Object.assign({}, stateParams), params),
                }), () => resolve());
            });
        }
        getInjectedProps(_props, state) {
            const { createAsyncStatus, getItemAsyncStatus, getItemsAsyncStatus, item, items, removeAsyncStatus, updateAsyncStatus, } = state;
            return this.memoizedGetInjectedProps(createAsyncStatus, getItemAsyncStatus, getItemsAsyncStatus, item, items, removeAsyncStatus, updateAsyncStatus);
        }
        memoizedGetInjectedProps(createAsyncStatus, getItemAsyncStatus, getItemsAsyncStatus, item, items, removeAsyncStatus, updateAsyncStatus) {
            return {
                create: this.create,
                createAsyncStatus,
                getItem: this.getItem,
                getItemAsyncStatus,
                getItems: this.getItems,
                getItemsAsyncStatus,
                item,
                items,
                remove: this.remove,
                removeAsyncStatus,
                update: this.update,
                updateAsyncStatus,
                paramsChange: this.paramsChange,
            };
        }
        render() {
            return (_jsx(Component, Object.assign({}, this.props, { flows: this.getInjectedProps(this.props, this.state) })));
        }
    }
    __decorate([
        boundMethod
    ], WithFlows.prototype, "create", null);
    __decorate([
        boundMethod
    ], WithFlows.prototype, "getItem", null);
    __decorate([
        boundMethod
    ], WithFlows.prototype, "getItems", null);
    __decorate([
        boundMethod
    ], WithFlows.prototype, "update", null);
    __decorate([
        boundMethod
    ], WithFlows.prototype, "remove", null);
    __decorate([
        boundMethod
    ], WithFlows.prototype, "paramsChange", null);
    __decorate([
        boundMethod
    ], WithFlows.prototype, "getInjectedProps", null);
    return WithFlows;
};
